/**
 * Punjabi (Gurmukhi) messages — primary locale for the field-rep app.
 *
 * Translation policy (per product call, May 2026; revised after field feedback
 * May 28 — caste/pump-type/unit labels and proper nouns like Android/GPS are
 * now transliterated into Gurmukhi so a Punjabi-first reader never hits a
 * cold Latin word mid-sentence):
 *  • All instructions, questions, labels: Gurmukhi.
 *  • Variety codes (PR 113, ADV 9293), digit-bearing identifiers, and SI
 *    abbreviations (L/sec, kL, m³/hr, HP, kW) stay Latin.
 *  • Full-word units (Feet, Metre) and category acronyms (General/OBC/SC/ST,
 *    Centrifugal/Submersible, Android, GPS) are transliterated — same sound,
 *    Gurmukhi script.
 *  • Free-text answers the surveyor types stay English (analytics + backend
 *    stability) — placeholders explicitly tell them so.
 *
 * Keep this in lock-step with en.ts. Any key added here must also exist in
 * en.ts so missing-translation fallback works.
 */
export default {
  splash: {
    badge: 'ਅਧਿਕਾਰਤ ਫੀਲਡ ਵਰਤੋਂ',
    titleWord1: 'ਵਾਟਰ',
    titleWord2: 'ਸਰਵੇ',
    subtitle: 'ਪੰਪ ਇੰਸਟਾਲੇਸ਼ਨ ਤੇ ਆਡਿਟ ਲਈ ਫੀਲਡ ਡਾਟਾ ਇਕੱਠਾ ਕਰਨ ਵਾਲੀ ਐਪ',
    step1: 'IMEI ਸਕੈਨ ਕਰੋ',
    step2: 'ਜਾਣਕਾਰੀ ਭਰੋ',
    step3: 'ਫੋਟੋਆਂ ਅੱਪਲੋਡ ਕਰੋ',
    chips: {
      offline: 'ਆਫਲਾਈਨ ਵੀ ਚੱਲੇ',
      secure: 'ਸੁਰੱਖਿਅਤ ਡਾਟਾ',
      sync: 'ਤੁਰੰਤ ਸਿੰਕ',
    },
    cta: 'ਸਰਵੇ ਸ਼ੁਰੂ ਕਰੋ',
    installer: 'ਇੰਸਟਾਲਰ ਲੌਗਇਨ (ਇੰਸਟਾਲੇਸ਼ਨ ਅਤੇ ਫੀਲਡ ਟੈਸਟ)',
    foot: 'Powered by Wattr · wattr.ai',
  },
  whatNext: {
    newTitle: 'ਨਵਾਂ ਸਰਵੇ ਸ਼ੁਰੂ ਕਰੋ',
    newSub: 'ਜਿਹੜਾ ਪੰਪ ਅਜੇ ਨਹੀਂ ਭੇਜਿਆ, ਉਸ ਦਾ ਸਰਵੇ ਕਰੋ',
    pastTitle: 'ਭੇਜੇ ਹੋਏ ਸਰਵੇ',
    pastSub: 'ਇਸ ਨੰਬਰ ਤੋਂ ਭੇਜੇ ਸਰਵੇ ਵੇਖੋ',
    pastNone: 'ਹਾਲੇ ਕੋਈ ਨਹੀਂ ਭੇਜਿਆ',
    needsFix: '{n} ਸਰਵੇ ਵਿੱਚ ਸੁਧਾਰ ਕਰਨਾ ਹੈ | {n} ਸਰਵੇਆਂ ਵਿੱਚ ਸੁਧਾਰ ਕਰਨਾ ਹੈ',
    back: 'ਪਿੱਛੇ',
  },
  ui: {
    appTitle: 'ਵਾਟਰ ਸਰਵੇ',
    appSubtitle: 'ਫੀਲਡ ਇਨਟੇਕ · {n}-ਪੜਾਅ',
    online: 'ਆਨਲਾਈਨ',
    offline: 'ਆਫਲਾਈਨ',
    syncing: 'ਸਿੰਕ ਹੋ ਰਿਹਾ…',
    unsynced: '{n} ਅਣ-ਸਿੰਕ',
    syncToastSent: '{n} ਸਰਵੇ ਸਿੰਕ ਹੋ ਗਏ',
    syncToastNone: 'ਸਿੰਕ ਕਰਨ ਲਈ ਕੁਝ ਨਹੀਂ',
    syncToastBlocked: '{n} ਡਰਾਫਟ ਭੇਜੇ ਨਹੀਂ ਜਾ ਸਕਦੇ — ਵੇਖਣ ਲਈ ਚੇਤਾਵਨੀ ਦਬਾਓ',
    blockedPill: '{n} ਨੂੰ ਧਿਆਨ ਚਾਹੀਦਾ',
    blockedTitle: 'ਤੁਹਾਡੇ ਧਿਆਨ ਦੀ ਲੋੜ ਵਾਲੇ ਡਰਾਫਟ',
    blockedBody: 'ਇਹ ਵਾਰ-ਵਾਰ ਭੇਜਣ ਦੀ ਕੋਸ਼ਿਸ਼ ਮਗਰੋਂ ਰੁਕ ਗਏ ਹਨ। ਇਹ ਆਪਣੇ ਆਪ ਨਹੀਂ ਜਾਣਗੇ — ਕਾਰਨ ਠੀਕ ਹੋਣ ਤੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ, ਜਾਂ ਹਟਾ ਦਿਓ।',
    blockedRetry: 'ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼',
    blockedDiscard: 'ਹਟਾਓ',
    blockedDiscarded: 'ਡਰਾਫਟ ਹਟਾ ਦਿੱਤਾ',
    blockedAttempts: '{n} ਵਾਰ ਕੋਸ਼ਿਸ਼ ਕੀਤੀ',
    blockedUnknownError: 'ਅਣਪਛਾਤੀ ਗਲਤੀ',
    syncToastOffline: 'ਆਫਲਾਈਨ — ਨੈੱਟ ਆਉਂਦੇ ਹੀ ਸਿੰਕ ਹੋਵੇਗਾ',
    syncToastFailed: 'ਸਿੰਕ ਅਸਫਲ — ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰਾਂਗੇ',
    syncToastFailedReason: 'ਸਿੰਕ ਅਸਫਲ: {reason}',
    back: 'ਪਿੱਛੇ',
    continue: 'ਅੱਗੇ',
    submit: 'ਸਰਵੇ ਜਮ੍ਹਾਂ ਕਰੋ',
    selectPlaceholder: 'ਚੁਣੋ…',
    selectYearPlaceholder: 'ਸਾਲ ਚੁਣੋ…',
    selectCropPlaceholder: 'ਫਸਲ ਚੁਣੋ…',
    selectAfterCrop: 'ਪਹਿਲਾਂ ਫਸਲ ਚੁਣੋ',
    typeVarietyName: 'ਕਿਸਮ ਦਾ ਨਾਮ ਲਿਖੋ',
    yes: 'ਹਾਂ',
    no: 'ਨਹੀਂ',
    other: 'ਹੋਰ',
    otherCropOption: 'ਹੋਰ (ਸੂਚੀ ਵਿੱਚ ਨਹੀਂ)',
    otherCropName: 'ਫਸਲ ਦਾ ਨਾਮ',
    otherVarietyName: 'ਕਿਸਮ ਦਾ ਨਾਮ',
    otherCropNamePlaceholder: 'ਫਸਲ ਦਾ ਨਾਮ ਅੰਗਰੇਜ਼ੀ ਵਿੱਚ ਲਿਖੋ',
    otherVarietyNamePlaceholder: 'ਕਿਸਮ ਦਾ ਨਾਮ ਅੰਗਰੇਜ਼ੀ ਵਿੱਚ ਲਿਖੋ',
    addAnotherCrop: 'ਹੋਰ ਫਸਲ ਜੋੜੋ',
    removeCrop: 'ਇਹ ਫਸਲ ਹਟਾਓ',
    noVarieties: 'ਇਸ ਫਸਲ ਲਈ ਕੋਈ ਨਾਮਜ਼ਦ ਕਿਸਮ ਨਹੀਂ — ਚੋਣ ਸੰਭਾਲ ਲਈ।',
    varietiesHeader: 'ਕਿਸਮ (ਇੱਕ ਚੁਣੋ)',
    requiredMark: '*',
    optionalTag: 'ਚੋਣਵੀਂ',
    otherNotInList: 'ਹੋਰ (ਸੂਚੀ ਵਿੱਚ ਨਹੀਂ)',
    otherSpecifyPlaceholder: '{label} ਦਾ ਨਾਮ ਲਿਖੋ',
    otherBackToList: '← ਸੂਚੀ ਵਿੱਚੋਂ ਚੁਣੋ',
    reset: 'ਰੀਸੈੱਟ',
    cancel: 'ਰੱਦ ਕਰੋ',
    startAnother: 'ਨਵਾਂ ਸਰਵੇ ਸ਼ੁਰੂ ਕਰੋ',
    surveySaved: 'ਸਰਵੇ ਸੰਭਾਲ ਲਿਆ',
    autofilledHint: 'ਯੰਤਰ ਰਿਕਾਰਡ ਤੋਂ ਆਟੋ-ਭਰਿਆ · ਲੋੜ ਪਵੇ ਤਾਂ ਬਦਲੋ',
    autofilledBanner: 'ਯੰਤਰ ਰਿਕਾਰਡ ਤੋਂ {n} ਫੀਲਡ ਆਟੋ-ਭਰੇ।',
    submitBannerSingle: '{msg}',
    submitBannerMulti: 'ਅੱਗੇ ਜਾਣ ਤੋਂ ਪਹਿਲਾਂ {n} ਜ਼ਰੂਰੀ ਫੀਲਡ ਭਰੋ।',
    submitGate: 'ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਉਜਾਗਰ ਕੀਤੇ ਫੀਲਡ ਪੂਰੇ ਕਰੋ।',
    submitSavedSent: 'ਸਰਵੇ ਸੰਭਾਲ ਕੇ ਭੇਜ ਦਿੱਤਾ।',
    submitSavedRetry: 'ਫੋਨ ਵਿੱਚ ਸੰਭਾਲ ਲਿਆ। ਨੈੱਟ ਆਉਂਦੇ ਹੀ ਦੁਬਾਰਾ ਭੇਜਣ ਦੀ ਕੋਸ਼ਿਸ਼ ਕਰਾਂਗੇ।',
    submitSaved: 'ਸਰਵੇ ਸੰਭਾਲ ਲਿਆ।',
    submitOffline: 'ਆਫਲਾਈਨ — ਫੋਨ ਵਿੱਚ ਸੰਭਾਲ ਲਿਆ। ਨੈੱਟ ਆਉਂਦੇ ਹੀ ਸਿੰਕ ਕਰਾਂਗੇ।',
    submitFailFallback: 'ਡਰਾਫਟ ਸੰਭਾਲ ਨਹੀਂ ਹੋਇਆ। ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    purposeLockedHint: 'ਫਿਲਹਾਲ ਸਿਰਫ਼ ਆਡਿਟ ਉਪਲਬਧ ਹੈ। ਮੁਰੰਮਤ ਤੇ ਇੰਸਟਾਲੇਸ਼ਨ ਜਲਦੀ ਆ ਰਹੇ ਹਨ।',
    searchablePlaceholder: '{label} ਖੋਜੋ…',
    searchableStrictPlaceholder: '{label} ਚੁਣਨ ਲਈ ਟੈਪ ਕਰੋ',
    searchableSearching: 'ਖੋਜ ਜਾਰੀ…',
    searchableNoMatches: 'ਕੁਝ ਨਹੀਂ ਮਿਲਿਆ — ਟਾਈਪ ਕਰਦੇ ਰਹੋ।',
    photoCapturing: 'ਫੋਟੋ ਲੈ ਰਹੇ…',
    photoAdd: 'ਜੋੜੋ',
    cameraDeniedText: 'IMEI ਕੋਡ ਸਕੈਨ ਕਰਨ ਲਈ ਕੈਮਰੇ ਦੀ ਇਜਾਜ਼ਤ ਚਾਹੀਦੀ ਹੈ।',
    cameraOpenSettings: 'ਸੈਟਿੰਗਜ਼ ਖੋਲ੍ਹੋ',
    scanLabel: 'ਸਕੈਨ',
    scanCancel: 'ਰੱਦ',
    scanHint: 'ਸਥਿਰ ਰੱਖੋ — ਕੈਮਰਾ QR ਜਾਂ ਬਾਰਕੋਡ ਉੱਤੇ ਰੱਖੋ',
    scanZoomHint: 'ਛੋਟਾ QR? ਜ਼ੂਮ ਕਰਨ ਲਈ ਪਿੰਚ ਕਰੋ ਜਾਂ ਸਲਾਈਡਰ ਵਰਤੋ।',
    scanPhotoLabel: '🖼️ ਫੋਟੋ ਤੋਂ ਛੋਟਾ QR ਸਕੈਨ ਕਰੋ',
    scanPhotoScanning: 'ਫੋਟੋ ਪੜ੍ਹ ਰਹੇ ਹਾਂ…',
    scanNoCode: 'ਕੋਈ QR/IMEI ਨਹੀਂ ਮਿਲਿਆ। ਕੋਡ ਨੂੰ ਫਰੇਮ ਵਿੱਚ ਭਰੋ, ਸਥਿਰ ਰੱਖੋ ਤੇ ਫੇਰ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    scanTorchOn: '🔦 ਬੰਦ',
    scanTorchOff: '🔦 ਟਾਰਚ',
    scanWebOnly: 'ਸਕੈਨ ਸਿਰਫ਼ ਨੇਟਿਵ ਐਪ ਵਿੱਚ ਉਪਲਬਧ ਹੈ। IMEI ਹੱਥੀਂ ਟਾਈਪ ਕਰੋ।',
    scanPermDenied: 'ਕੈਮਰੇ ਦੀ ਇਜਾਜ਼ਤ ਨਹੀਂ ਮਿਲੀ — ਸੈਟਿੰਗਜ਼ ਵਿੱਚ ਜਾ ਕੇ ਚਾਲੂ ਕਰੋ।',
    headerThemeAria: 'ਥੀਮ: {choice}। ਬਦਲਣ ਲਈ ਟੈਪ ਕਰੋ।',
    headerLangAria: 'ਮੌਜੂਦਾ ਭਾਸ਼ਾ: ਪੰਜਾਬੀ। ਬਦਲ ਕੇ English ਕਰਨ ਲਈ ਟੈਪ ਕਰੋ।',
    langShortPa: 'ਪੰ',
    langShortEn: 'EN',
    syncRetryTitle: 'ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਲਈ ਟੈਪ ਕਰੋ',
    syncLastTitle: 'ਆਖਰੀ ਸਿੰਕ {time}',
    resetTitle: 'ਸਰਵੇ ਡਾਟਾ ਰੀਸੈੱਟ ਕਰਨਾ?',
    resetBody: 'ਇਹ ਫੋਨ ਉੱਤੇ ਹਰ ਫੀਲਡ ਖਾਲੀ ਕਰ ਦੇਵੇਗਾ — IMEI, ਟਿਕਾਣਾ, ਕਿਸਾਨ ਜਾਣਕਾਰੀ, ਪੰਪ ਤੇ ਸਿੰਚਾਈ ਦੇ ਜਵਾਬ — ਅਤੇ ਤੁਹਾਨੂੰ ਪਹਿਲੇ ਪੜਾਅ ਉੱਤੇ ਲੈ ਜਾਏਗਾ।',
    resetWarnDrafts: 'ਫੋਨ ਉੱਤੇ {n} ਅਣ-ਸਿੰਕ ਡਰਾਫਟ ਹੈ।',
    resetDiscardToggle: '{n} ਅਣ-ਸਿੰਕ ਡਰਾਫਟ ਵੀ ਮਿਟਾ ਦਿਓ',
    resetDiscardHint: 'ਇਹ ਵਾਪਸ ਨਹੀਂ ਹੋਵੇਗਾ। ਬੰਦ ਛੱਡੋ ਤਾਂ ਡਰਾਫਟ ਕਤਾਰ ਵਿੱਚ ਰਹਿਣਗੇ।',
    resetConfirm: 'ਰੀਸੈੱਟ ਕਰੋ',
    resetting: 'ਰੀਸੈੱਟ ਹੋ ਰਿਹਾ…',
    resetToastCleared: 'ਰੀਸੈੱਟ ਹੋ ਗਿਆ · {n} ਡਰਾਫਟ ਮਿਟਾਏ',
    resetToastReady: 'ਰੀਸੈੱਟ ਹੋ ਗਿਆ · ਨਵੇਂ ਸਰਵੇ ਲਈ ਤਿਆਰ',
    consentTitle: 'ਸਰਵੇ ਆਡਿਟ ਵਿੱਚ ਮਦਦ ਕਰੋ?',
    consentLead: 'ਅਸੀਂ ਹਰ ਸਰਵੇ ਨਾਲ ਥੋੜ੍ਹੀ ਜਿਹੀ ਪ੍ਰਸੰਗ ਜਾਣਕਾਰੀ ਜੋੜ ਸਕਦੇ ਹਾਂ:',
    consentItem1Strong: 'ਯੰਤਰ ਦਾ ਮਾਡਲ + ਐਂਡਰਾਇਡ ਵਰਜ਼ਨ',
    consentItem1: ' — ਯੰਤਰ-ਵਾਰ ਐਪ ਕਰੈਸ਼ ਦੀ ਜਾਂਚ ਲਈ।',
    consentItem2Strong: 'ਜਮ੍ਹਾਂ ਕਰਨ ਵੇਲੇ ਜੀ.ਪੀ.ਐੱਸ. ਜਗ੍ਹਾ',
    consentItem2: ' — ਜੇ 50 ਸਰਵੇ ਇੱਕੋ ਜਗ੍ਹਾ ਤੋਂ ਆਉਣ ਤਾਂ ਧੋਖੇ ਦੀ ਨਿਸ਼ਾਨੀ।',
    consentItem3Strong: 'ਨੈੱਟ ਕਿਸਮ + ਬੈਟਰੀ',
    consentItem3: ' — ਸਿੰਕ ਫੇਲ੍ਹ ਹੋਣ ਦੀ ਵਜ੍ਹਾ ਸਮਝਣ ਲਈ।',
    consentItem4Strong: 'ਭਰਨ ਵਿੱਚ ਲੱਗਾ ਸਮਾਂ',
    consentItem4: ' — ਬਹੁਤ ਛੇਤੀ ਭਰੇ ਸਰਵੇ ਫੜਨ ਲਈ।',
    consentFoot: 'ਅਸੀਂ ਤੁਹਾਡੇ ਫੋਨ ਦਾ IMEI, ਸੰਪਰਕ ਜਾਂ ਇੰਸਟਾਲ ਐਪਾਂ ਨਹੀਂ ਪੜ੍ਹਦੇ। ਨਾਂਹ ਕਰ ਸਕਦੇ ਹੋ — ਸਰਵੇ ਫਿਰ ਵੀ ਚਲਦਾ ਹੈ।',
    consentDeny: 'ਹੁਣੇ ਨਹੀਂ',
    consentAllow: 'ਇਜਾਜ਼ਤ ਦਿਓ',
  },
  mySurveys: {
    fixNow: 'ਹੁਣੇ ਠੀਕ ਕਰੋ',
    title: 'ਮੇਰੇ ਸਰਵੇ',
    subtitle: 'ਆਪਣਾ ਫੋਨ ਨੰਬਰ ਪਾਓ — ਤੁਹਾਡੇ ਪਿਛਲੇ ਸਰਵੇ ਏਥੇ ਨਜ਼ਰ ਆਉਣਗੇ। ਸਿਰਫ਼ ਦੁਬਾਰਾ-ਸਰਵੇ ਵਾਲੇ ਹੀ ਮੁੜ ਖੋਲ੍ਹ ਕੇ ਸੋਧੇ ਜਾ ਸਕਦੇ ਹਨ।',
    phonePlaceholder: '10-ਅੰਕੀ ਫੋਨ ਨੰਬਰ',
    lookup: 'ਖੋਜੋ',
    loading: 'ਖੋਜ ਰਹੇ…',
    opening: 'ਖੋਲ੍ਹ ਰਹੇ…',
    edit: 'ਸੋਧੋ',
    empty: 'ਇਸ ਨੰਬਰ ਉੱਤੇ ਅਜੇ ਤੱਕ ਕੋਈ ਸਰਵੇ ਨਹੀਂ ਮਿਲਿਆ।',
    close: 'ਬੰਦ ਕਰੋ',
    noLocation: 'ਜਗ੍ਹਾ ਅਣਜਾਣ',
    viewingFor: '{phone} ਤੋਂ ਭੇਜੇ ਸਰਵੇ ਵਿਖਾ ਰਹੇ ਹਾਂ',
    changeNumber: 'ਨੰਬਰ ਬਦਲੋ',
    enterPhonePrompt: 'ਆਪਣੇ ਪਿਛਲੇ ਸਰਵੇ ਵੇਖਣ ਲਈ ਆਪਣਾ ਫੋਨ ਨੰਬਰ ਪਾਓ।',
    onlyResurveyEditable: 'ਜਿਹੜਾ ਵੀ ਸਰਵੇ ਮਨਜ਼ੂਰ ਨਹੀਂ ਹੋਇਆ, ਉਹ ਸੋਧਿਆ ਜਾ ਸਕਦਾ ਹੈ।',
    errInvalidPhone: 'ਫੋਨ ਨੰਬਰ ਪੂਰੇ 10 ਅੰਕਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ।',
    errFetch: 'ਸੂਚੀ ਲੋਡ ਨਹੀਂ ਹੋਈ। ਥੋੜ੍ਹੀ ਦੇਰ ਬਾਅਦ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    errEditLocked: 'ਇਹ ਸਰਵੇ ਹੁਣ ਸੋਧਿਆ ਨਹੀਂ ਜਾ ਸਕਦਾ।',
    errEditFetch: 'ਡਰਾਫਟ ਖੋਲ੍ਹ ਨਹੀਂ ਸਕੇ — ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    status: {
      pending: 'ਜਾਂਚ ਜਾਰੀ',
      review: 'ਸਮੀਖਿਆ ਹੇਠ',
      approved: 'ਮਨਜ਼ੂਰ',
      resurvey: 'ਦੁਬਾਰਾ ਸਰਵੇ',
    },
  },
  installer: {
    login: {
      badge: 'ਇੰਸਟਾਲਰ ਪਹੁੰਚ',
      title: 'ਇੰਸਟਾਲਰ ਸਾਈਨ-ਇਨ',
      subtitle: 'ਪੰਪ ਲਗਾਉਣ ਤੇ ਫੀਲਡ-ਟੈਸਟ ਲਈ ਸਾਈਨ-ਇਨ ਕਰੋ।',
      personaAria: 'ਇੰਸਟਾਲਰ ਦੀ ਕਿਸਮ ਚੁਣੋ',
      fcm: 'FCM',
      contract: 'ਠੇਕਾ ਕਾਮਾ',
      usernameLabel: 'ਯੂਜ਼ਰਨੇਮ',
      usernamePlaceholder: 'ਤੁਹਾਡਾ FCM ਯੂਜ਼ਰਨੇਮ',
      passwordLabel: 'ਪਾਸਵਰਡ',
      passwordPlaceholder: 'ਤੁਹਾਡਾ FCM ਪਾਸਵਰਡ',
      phoneLabel: 'ਫ਼ੋਨ ਨੰਬਰ',
      phonePlaceholder: '10-ਅੰਕਾਂ ਦਾ ਮੋਬਾਈਲ',
      contractHint: 'ਠੇਕਾ ਕਾਮੇ ਸਿਰਫ਼ ਆਪਣੇ ਫ਼ੋਨ ਨੰਬਰ ਨਾਲ ਸਾਈਨ-ਇਨ ਕਰਦੇ ਹਨ — ਪਾਸਵਰਡ ਦੀ ਲੋੜ ਨਹੀਂ।',
      signIn: 'ਸਾਈਨ ਇਨ',
      signingIn: 'ਸਾਈਨ-ਇਨ ਹੋ ਰਿਹਾ ਹੈ…',
      errFcmRequired: 'ਆਪਣਾ ਯੂਜ਼ਰਨੇਮ ਤੇ ਪਾਸਵਰਡ ਦੋਵੇਂ ਭਰੋ।',
      errPhoneInvalid: 'ਫ਼ੋਨ ਨੰਬਰ ਠੀਕ 10 ਅੰਕਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ।',
      foot: 'Powered by Wattr · wattr.ai',
    },
    home: {
      title: 'ਇੰਸਟਾਲਰ ਹੋਮ',
      comingSoon: 'ਇੰਸਟਾਲਰ ਹੋਮ — ਜਲਦੀ ਆ ਰਿਹਾ ਹੈ',
      welcome: 'ਤੁਸੀਂ ਕੀ ਕਰਨਾ ਚਾਹੁੰਦੇ ਹੋ?',
      persona: 'ਕਿਸਮ',
      personaFcm: 'FCM',
      personaContract: 'ਠੇਕਾ ਕਾਮਾ',
      logout: 'ਸਾਈਨ ਆਉਟ',
    },
    nav: {
      pending: 'ਬਾਕੀ ਫੀਲਡ ਟੈਸਟ',
      pendingDesc: 'ਫੀਲਡ ਟੈਸਟ ਦੀ ਉਡੀਕ ਵਿੱਚ ਪੰਪ',
      newInstallation: 'ਨਵੀਂ ਇੰਸਟਾਲੇਸ਼ਨ',
      newInstallationDesc: 'ਕਿਸਾਨ, ਪੰਪ ਤੇ ਡਿਵਾਈਸ ਰਜਿਸਟਰ ਕਰੋ',
      fieldTest: 'ਫੀਲਡ ਟੈਸਟ',
      checklist: 'ਚੈੱਕਲਿਸਟ',
      contractUnavailable: 'ਠੇਕਾ ਕਾਮਿਆਂ ਲਈ ਉਪਲਬਧ ਨਹੀਂ',
    },
  },
  validation: {
    required: '{label} ਜ਼ਰੂਰੀ ਹੈ।',
    digitsOnly: '{label} ਸਿਰਫ਼ ਅੰਕਾਂ ਵਿੱਚ ਲਿਖੋ।',
    exactLength: '{label} ਠੀਕ {n} ਅੰਕਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ।',
    patternInvalid: '{label} ਦਾ ਫਾਰਮੈਟ ਗਲਤ ਹੈ।',
    min: '{label} {n} ਤੋਂ ਘੱਟ ਨਹੀਂ ਹੋਣਾ ਚਾਹੀਦਾ।',
    max: '{label} {n} ਤੋਂ ਵੱਧ ਨਹੀਂ ਹੋਣਾ ਚਾਹੀਦਾ।',
    imeiAntiBlank: 'IMEI ਠੀਕ {n} ਅੰਕਾਂ ਦਾ ਹੋਣਾ ਚਾਹੀਦਾ ਹੈ।',
    farmerAntiBlank: 'ਜਮ੍ਹਾਂ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਕਿਸਾਨ ਦਾ ਨਾਮ ਜਾਂ ਫੋਨ ਨੰਬਰ ਭਰੋ।',
    missingPhotos: 'ਫੋਟੋਆਂ ਗਾਇਬ: {names}।',
    varietyRequired: '{label} ਵਿੱਚ ਹਰ ਫਸਲ ਲਈ ਕਿਸਮ ਚੁਣੋ।',
  },
  correction: {
    title: '{n} ਖਾਨੇ ਠੀਕ ਕਰਨੇ ਹਨ',
    body: 'ਸਾਡੀ ਟੀਮ ਨੇ ਤੁਹਾਡਾ ਸਰਵੇਖਣ ਜਾਂਚਿਆ ਹੈ। ਸਿਰਫ਼ ਹਾਈਲਾਈਟ ਕੀਤੇ ਖਾਨੇ ਬਦਲਣੇ ਹਨ — ਬਾਕੀ ਸਭ ਪੁਸ਼ਟੀ ਹੋ ਚੁੱਕੇ ਹਨ।',
    jump: 'ਉੱਥੇ ਲੈ ਚੱਲੋ',
    fieldNeedsFix: 'ਠੀਕ ਕਰਨਾ ਹੈ',
    fieldConfirmed: 'ਸਾਡੀ ਟੀਮ ਵੱਲੋਂ ਪੁਸ਼ਟੀ',
    needsFixBadge: '{n} ਠੀਕ ਕਰਨੇ',
  },
  steps: {
    surveyor: {
      title: 'ਸਰਵੇਅਰ ਦੀ ਜਾਣਕਾਰੀ',
      subtitle: 'ਦੱਸੋ ਇਹ ਡਾਟਾ ਕੌਣ ਇਕੱਠਾ ਕਰ ਰਿਹਾ ਹੈ — ਰਿਕਾਰਡ ਲਈ ਜ਼ਰੂਰੀ ਹੈ।',
    },
    previous_surveys: {
      title: 'ਤੁਸੀਂ ਕੀ ਕਰਨਾ ਚਾਹੁੰਦੇ ਹੋ?',
      subtitle: 'ਇੱਕ ਚੁਣੋ।',
    },
    purpose: {
      title: 'ਸਰਵੇ ਦਾ ਮਕਸਦ',
      subtitle: 'ਫਿਲਹਾਲ ਸਿਰਫ਼ ਆਡਿਟ ਉਪਲਬਧ ਹੈ। ਮੁਰੰਮਤ ਤੇ ਇੰਸਟਾਲੇਸ਼ਨ ਜਲਦੀ ਆ ਰਹੇ ਹਨ।',
    },
    installation_option: {
      title: 'ਇੰਸਟਾਲੇਸ਼ਨ ਚੋਣ',
      subtitle: 'ਸਰਵੇ ਨਵੇਂ / ਪ੍ਰੀ-ਇੰਸਟਾਲੇਸ਼ਨ ਲਈ ਹੈ। ਆਡਿਟ ਪਹਿਲਾਂ ਤੋਂ ਲੱਗੇ ਯੰਤਰ ਲਈ ਹੈ।',
    },
    device: {
      title: 'ਯੰਤਰ ਦੀ ਪਛਾਣ',
      subtitle: 'ਯੰਤਰ ਉੱਤੇ ਲੱਗੇ ਲੇਬਲ ਤੋਂ 15-ਅੰਕੀ IMEI ਸਕੈਨ ਜਾਂ ਟਾਈਪ ਕਰੋ।',
    },
    location: {
      title: 'ਟਿਕਾਣਾ',
      subtitle: 'ਰਾਜ ਯੰਤਰ ਤੋਂ ਆਟੋ-ਭਰਦਾ ਹੈ; ਜ਼ਿਲ੍ਹਾ ਤੇ ਬਲਾਕ ਸੂਚੀ ਵਿੱਚੋਂ ਚੁਣੋ।',
    },
    farmer: {
      title: 'ਕਿਸਾਨ ਦੀ ਜਾਣਕਾਰੀ',
    },
    pump: {
      title: 'ਪੰਪ ਤੇ ਫੀਡਰ',
    },
    discharge: {
      title: 'ਪੰਪ ਦੀ ਨਿਕਾਸੀ',
      subtitle: 'ਨਿਕਾਸੀ ਉਦੋਂ ਹੀ ਮਾਪੀ ਜਾ ਸਕਦੀ ਹੈ ਜਦੋਂ ਪੰਪ ਚਾਲੂ ਹੋਵੇ। ਜੇ ਬਿਜਲੀ ਨਹੀਂ ਹੈ ਤਾਂ "ਨਹੀਂ" ਚੁਣ ਕੇ ਅੱਗੇ ਵਧੋ।',
    },
    irrigation: {
      title: 'ਸਿੰਚਾਈ ਦੇ ਤਰੀਕੇ',
    },
    crops: {
      title: 'ਉਗਾਈਆਂ ਫਸਲਾਂ',
      subtitle: 'ਖਰੀਫ਼ ਜ਼ਰੂਰੀ ਹੈ; ਹਾੜੀ ਤੇ ਜ਼ੈਦ ਚੋਣਵੀਆਂ ਹਨ (ਇੱਕ-ਫਸਲੀ ਹੋਵੇ ਤਾਂ ਖਾਲੀ ਛੱਡੋ)।',
    },
    photos: {
      title: 'ਫੋਟੋਆਂ',
      subtitle: 'ਲੋੜੀਂਦੀਆਂ ਫੋਟੋਆਂ ਖਿੱਚੋ — ਚੈੱਕਲਿਸਟ ਦੌਰੇ ਦੇ ਮਕਸਦ ਅਨੁਸਾਰ ਹੈ।',
    },
  },
  fields: {
    surveyor_name: {
      label: 'ਤੁਹਾਡਾ ਨਾਮ (ਚੋਣਵਾਂ)',
      placeholder: 'ਪੂਰਾ ਨਾਮ (ਚੋਣਵਾਂ)',
      help: 'ਚੋਣਵਾਂ — ਪਹਿਲੀ ਵਾਰ ਭਰਨ ਤੋਂ ਬਾਅਦ ਇਸ ਫੋਨ ਉੱਤੇ ਸੰਭਾਲਿਆ ਜਾਏਗਾ।',
    },
    surveyor_contact: {
      label: 'ਤੁਹਾਡਾ ਫੋਨ ਨੰਬਰ',
      placeholder: '10-ਅੰਕੀ ਮੋਬਾਈਲ',
      help: 'ਤੁਹਾਡਾ ਫੋਨ ਨੰਬਰ ਹੀ ਤੁਹਾਡੀ ID ਹੈ — ਅਗਲੀ ਸਕਰੀਨ ਉੱਤੇ ਇਸ ਨੰਬਰ ਤੋਂ ਭੇਜੇ ਸਰਵੇ ਦਿਸਣਗੇ।',
    },
    surveyor_employee_id: {
      label: 'ਮੁਲਾਜ਼ਮ / ਵੈਂਡਰ ਆਈ.ਡੀ. (ਚੋਣਵੀਂ)',
      placeholder: 'ਜੇ ਤੁਹਾਡੀ ਸੰਸਥਾ ਨੇ ਦਿੱਤੀ ਹੋਵੇ',
    },
    survey_purpose: {
      label: 'ਦੌਰੇ ਦਾ ਮਕਸਦ',
      help: 'ਇੰਸਟਾਲੇਸ਼ਨ = ਪਹਿਲੀ ਵਾਰ ਲਾਉਣਾ · ਮੁਰੰਮਤ = ਖਰਾਬੀ ਠੀਕ ਕਰਨੀ · ਆਡਿਟ = ਸਿਰਫ਼ ਨਿਰੀਖਣ।',
      options: {
        installation: 'ਇੰਸਟਾਲੇਸ਼ਨ',
        repair: 'ਮੁਰੰਮਤ',
        audit: 'ਆਡਿਟ',
      },
    },
    survey_mode: {
      label: 'ਸਰਵੇ ਦੀ ਕਿਸਮ',
      options: {
        with_existing_device: 'ਯੰਤਰ ਪਹਿਲਾਂ ਤੋਂ ਲੱਗਾ ਹੈ',
        new_pre_installation: 'ਨਵਾਂ / ਪ੍ਰੀ-ਇੰਸਟਾਲੇਸ਼ਨ ਸਰਵੇ',
      },
    },
    installation_option: {
      label: 'ਇੰਸਟਾਲੇਸ਼ਨ ਚੋਣ',
      help: 'ਸਰਵੇ = ਨਵਾਂ ਜਾਂ ਪ੍ਰੀ-ਇੰਸਟਾਲੇਸ਼ਨ · ਆਡਿਟ = ਪਹਿਲਾਂ ਤੋਂ ਲੱਗੇ ਯੰਤਰ ਦਾ ਨਿਰੀਖਣ / ਮੁਰੰਮਤ।',
      options: {
        survey: 'ਸਰਵੇ (ਨਵਾਂ / ਪ੍ਰੀ-ਇੰਸਟਾਲੇਸ਼ਨ)',
        audit: 'ਆਡਿਟ (ਪਹਿਲਾਂ ਤੋਂ ਲੱਗਾ ਯੰਤਰ)',
      },
    },
    tenant_id: {
      label: 'ਟੈਨੈਂਟ / ਸੰਸਥਾ',
      help: 'ਉਹ ਟੈਨੈਂਟ ਚੁਣੋ ਜਿਸ ਅਧੀਨ ਇਹ ਪੰਪ ਆਉਂਦਾ ਹੈ — ਕਿਸਾਨ ਦੀ ਲੌਗਇਨ ਉਸੇ ਅਧੀਨ ਬਣਦੀ ਹੈ।',
    },
    imei_number: {
      label: 'IMEI ਨੰਬਰ',
      placeholder: '15-ਅੰਕੀ IMEI',
      help: 'ਯੰਤਰ ਉੱਤੇ ਲੱਗੇ QR / ਬਾਰਕੋਡ ਨੂੰ ਸਕੈਨ ਕਰਨ ਲਈ ਬਟਨ ਦਬਾਓ।',
    },
    state: {
      label: 'ਰਾਜ / ਕੇਂਦਰ ਸ਼ਾਸਿਤ ਪ੍ਰਦੇਸ਼',
      help: 'ਯੰਤਰ ਰਿਕਾਰਡ ਤੋਂ ਆਟੋ-ਭਰਿਆ; ਨਹੀਂ ਤਾਂ ਖੋਜ ਕੇ ਚੁਣੋ।',
    },
    district: {
      label: 'ਜ਼ਿਲ੍ਹਾ',
      help: 'ਮੈਪ ਹੋਵੇ ਤਾਂ ਆਟੋ-ਭਰਿਆ; ਨਹੀਂ ਤਾਂ ਸੂਚੀ ਵਿੱਚੋਂ ਚੁਣੋ।',
    },
    block: {
      label: 'ਬਲਾਕ',
      help: 'ਮੈਪ ਹੋਵੇ ਤਾਂ ਆਟੋ-ਭਰਿਆ; ਨਹੀਂ ਤਾਂ ਸੂਚੀ ਵਿੱਚੋਂ ਚੁਣੋ।',
    },
    village: { label: 'ਪਿੰਡ' },
    farmer_name: { label: 'ਕਿਸਾਨ ਦਾ ਨਾਮ' },
    farmer_father_name: { label: 'ਕਿਸਾਨ ਦੇ ਪਿਤਾ ਦਾ ਨਾਮ' },
    farmer_contact_number: {
      label: 'ਕਿਸਾਨ ਦਾ ਫੋਨ ਨੰਬਰ',
      placeholder: '10-ਅੰਕੀ ਮੋਬਾਈਲ',
    },
    farmer_caste: {
      label: 'ਕਿਸਾਨ ਦੀ ਜਾਤ',
      // Acronyms transliterated to Gurmukhi — reads identically to the English
      // sound (jan-ral / oh-bee-see / es-see / es-tee) so a Punjabi-first
      // reader doesn't switch scripts mid-dropdown.
      options: {
        general: 'ਜਨਰਲ',
        obc: 'ਓ.ਬੀ.ਸੀ.',
        sc: 'ਐੱਸ.ਸੀ.',
        st: 'ਐੱਸ.ਟੀ.',
      },
    },
    feeder_name: {
      label: 'ਫੀਡਰ ਦਾ ਨਾਮ',
      help: 'ਮੈਪ ਹੋਵੇ ਤਾਂ ਆਟੋ-ਭਰਿਆ; ਨਹੀਂ ਤਾਂ ਸੂਚੀ ਵਿੱਚੋਂ ਚੁਣੋ ਜਾਂ ਨਾਮ ਲਿਖੋ।',
    },
    installation_date: {
      label: 'ਇੰਸਟਾਲੇਸ਼ਨ ਦੀ ਤਾਰੀਖ',
      help: 'ਮਿਲੇ ਤਾਂ ਆਟੋ-ਭਰਿਆ; ਨਹੀਂ ਤਾਂ ਤਾਰੀਖ ਚੁਣੋ।',
    },
    year_of_pump_installation: { label: 'ਪੰਪ ਲੱਗਣ ਦਾ ਸਾਲ' },
    pump_type: {
      label: 'ਪੰਪ ਦੀ ਕਿਸਮ',
      options: { centrifugal: 'ਸੈਂਟ੍ਰਿਫਿਊਗਲ', submersible: 'ਸਬਮਰਸੀਬਲ' },
    },
    starter_button_type: {
      label: 'ਸਟਾਰਟਰ ਦੀ ਕਿਸਮ',
      help: 'ਮੋਟਰ ਸਟਾਰਟਰ ਪੈਨਲ ਉੱਤੇ ਬਟਨਾਂ ਦੀ ਗਿਣਤੀ — ਅਸਲ ਸਟਾਰਟਰ ਵੇਖੋ, ਅੰਦਾਜ਼ਾ ਨਾ ਲਾਓ।',
      options: { '2': '2-ਸਟਾਰਟਰ (2-ਬਟਨ)', '3': '3-ਸਟਾਰਟਰ (3-ਬਟਨ)' },
    },
    horse_power: { label: 'ਹਾਰਸ ਪਾਵਰ (HP)' },
    depth_of_source: { label: 'ਪਾਣੀ ਦੇ ਸ੍ਰੋਤ ਦੀ ਡੂੰਘਾਈ' },
    outlet_pipe_diameter_in: {
      label: 'ਆਊਟਲੈੱਟ ਪਾਈਪ ਦਾ ਵਿਆਸ (ਇੰਚ)',
      help: 'ਆਊਟਲੈੱਟ ਪਾਈਪ ਦਾ ਅੰਦਰੂਨੀ ਵਿਆਸ ਇੰਚ ਵਿੱਚ।',
    },
    vertical_distance_in: {
      label: 'ਲੰਬਕਾਰ ਦੂਰੀ (ਇੰਚ)',
      help: 'ਪਾਈਪ ਦੇ ਮੂੰਹ ਤੋਂ ਪਾਣੀ ਦੀ ਧਾਰ ਦੀ ਲੰਬਕਾਰ ਡਿੱਗ।',
    },
    pump_discharge: {
      label: 'ਪੰਪ ਦੀ ਨਿਕਾਸੀ (ਆਟੋ-ਗਣਨਾ)',
      help: 'L/sec ਵਿੱਚ ਆਟੋ-ਗਣਨਾ — Q = 0.05963 × D × D × V (ਆਊਟਲੈੱਟ ਵਿਆਸ + ਲੰਬਕਾਰ ਦੂਰੀ ਤੋਂ)।',
    },
    pump_runtime_hours: {
      label: 'ਪੰਪ ਚੱਲਣ ਦਾ ਸਮਾਂ (ਘੰਟੇ)',
      help: 'ਔਸਤ ਰੋਜ਼ਾਨਾ ਚੱਲਣ ਦਾ ਸਮਾਂ।',
    },
    land_type: {
      label: 'ਖੇਤੀ ਜ਼ਮੀਨ ਦੀ ਕਿਸਮ',
      options: { owned: 'ਆਪਣੀ', rented: 'ਕਿਰਾਏ ਦੀ', leased: 'ਠੇਕੇ ਦੀ' },
    },
    field_boundary: {
      label: 'ਖੇਤ ਦੀ ਹੱਦਬੰਦੀ (ਚਾਰ ਕੋਨੇ ਮਾਪੋ)',
      help: 'ਖੇਤ ਦੇ ਚਾਰੇ ਕੋਨਿਆਂ ਉੱਤੇ ਜਾ ਕੇ ਜੀ.ਪੀ.ਐੱਸ. ਨਾਲ ਨਿਸ਼ਾਨ ਫੜੋ — ਚਾਰ ਨੁਕਤਿਆਂ ਤੋਂ ਖੇਤ ਦਾ ਨਕਸ਼ਾ ਬਣ ਜਾਏਗਾ।',
      hint: 'ਹਰ ਕੋਨੇ ਉੱਤੇ ਖੜ੍ਹੇ ਹੋ ਕੇ "ਕੋਨਾ ਫੜੋ" ਦਬਾਓ — ਘੜੀ-ਵਾਰ ਜਾਂ ਘੜੀ-ਉਲਟ ਕੋਈ ਵੀ ਤਰਤੀਬ ਚੱਲੇਗੀ।',
      captureCorner: 'ਕੋਨਾ {n}/{total} ਫੜੋ',
      capturing: 'ਜੀ.ਪੀ.ਐੱਸ. ਚਾਲੂ ਹੋ ਰਿਹਾ…',
      recapture: 'ਮੁੜ ਫੜੋ',
      cancel: 'ਰੱਦ ਕਰੋ',
      remap: 'ਮੁੜ ਮਾਪੋ',
      distance: 'ਘੇਰਾ',
      area: 'ਖੇਤਰਫਲ',
      mapHint: 'ਨਕਸ਼ਾ ਕੋਨੇ ਫੜਨ ਉੱਤੇ ਨਜ਼ਰ ਆਏਗਾ',
      errorPermission: 'ਜੀ.ਪੀ.ਐੱਸ. ਦੀ ਇਜਾਜ਼ਤ ਨਹੀਂ ਮਿਲੀ — ਸੈਟਿੰਗਜ਼ ਵਿੱਚੋਂ ਚਾਲੂ ਕਰੋ।',
      errorUnavailable: 'ਜੀ.ਪੀ.ਐੱਸ. ਉਪਲਬਧ ਨਹੀਂ।',
      errorTimeout: 'ਜੀ.ਪੀ.ਐੱਸ. ਨੇ ਜਵਾਬ ਨਹੀਂ ਦਿੱਤਾ — ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      addField: 'ਹੋਰ ਖੇਤ ਜੋੜੋ',
      fieldN: 'ਖੇਤ {n}',
      totalArea: 'ਕੁੱਲ ਖੇਤਰਫਲ',
      removeField: 'ਹਟਾਓ',
    },
    pump_location: {
      label: 'ਪੰਪ ਦੀ ਮੌਜੂਦਾ ਜੀ.ਪੀ.ਐੱਸ. ਜਗ੍ਹਾ',
      help: 'ਫੋਟੋਆਂ ਖਿੱਚਣ ਤੋਂ ਪਹਿਲਾਂ ਜੀ.ਪੀ.ਐੱਸ. ਨਾਲ ਪੰਪ ਦੀ ਅਸਲ ਜਗ੍ਹਾ ਫੜੋ — ਇਹ ਜ਼ਰੂਰੀ ਹੈ।',
      capture: 'ਮੌਜੂਦਾ ਜਗ੍ਹਾ ਫੜੋ',
      capturing: 'ਜੀ.ਪੀ.ਐੱਸ. ਚਾਲੂ ਹੋ ਰਿਹਾ…',
      recapture: 'ਦੁਬਾਰਾ ਫੜੋ',
      captured: 'ਜੀ.ਪੀ.ਐੱਸ. ਜਗ੍ਹਾ ਮਿਲ ਗਈ',
      accuracy: 'ਸਟੀਕਤਾ: ±{m} ਮੀਟਰ',
      photosLocked: 'ਫੋਟੋਆਂ ਖੋਲ੍ਹਣ ਲਈ ਉੱਪਰ ਜਗ੍ਹਾ ਫੜੋ।',
      errorPermission: 'ਜੀ.ਪੀ.ਐੱਸ. ਦੀ ਇਜਾਜ਼ਤ ਨਹੀਂ ਮਿਲੀ — ਸੈਟਿੰਗਜ਼ ਵਿੱਚੋਂ ਚਾਲੂ ਕਰੋ।',
      errorUnavailable: 'ਜੀ.ਪੀ.ਐੱਸ. ਉਪਲਬਧ ਨਹੀਂ — ਖੁੱਲ੍ਹੀ ਥਾਂ ਵਿੱਚ ਜਾ ਕੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      errorTimeout: 'ਜੀ.ਪੀ.ਐੱਸ. ਨੇ ਜਵਾਬ ਨਹੀਂ ਦਿੱਤਾ — ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    },
    pump_coverage_acres: { label: 'ਪੰਪ ਅਧੀਨ ਰਕਬਾ (ਏਕੜ)' },
    electricity_at_visit: {
      label: 'ਕੀ ਇਸ ਵੇਲੇ ਥਾਂ ਉੱਤੇ ਬਿਜਲੀ ਹੈ? (ਆਪਣੀ ਅੱਖੀਂ ਵੇਖੋ)',
      help: 'ਮੀਟਰ / ਮੁੱਖ ਸਵਿੱਚ ਆਪ ਵੇਖੋ — ਯੰਤਰ ਦੇ ਡਾਟਾ ਉੱਤੇ ਨਿਰਭਰ ਨਾ ਕਰੋ। ਤੁਹਾਡੇ ਜਵਾਬ ਮੁਤਾਬਕ ਅਸੀਂ ਨਿਕਾਸੀ ਮਾਪਣ ਦੇ ਸਵਾਲ ਪੁੱਛਾਂਗੇ।',
    },
    discharge_method: {
      label: 'ਨਿਕਾਸੀ ਕਿਵੇਂ ਮਾਪੋਗੇ?',
      help: 'ਫਲੋਮੀਟਰ: ਮੀਟਰ ਤੋਂ ਸਿੱਧਾ L/s ਵਿੱਚ ਪੜ੍ਹੋ। ਟ੍ਰੈਜੈਕਟਰੀ: ਪਾਈਪ ਵਿਆਸ ਤੇ ਪਾਣੀ ਦੇ ਡਿੱਗਣ ਤੋਂ ਅਸੀਂ ਹਿਸਾਬ ਲਾਉਂਦੇ ਹਾਂ।',
      options: { flowmeter: 'ਫਲੋਮੀਟਰ', trajectory: 'ਟ੍ਰੈਜੈਕਟਰੀ ਵਿਧੀ' },
    },
    flowmeter_discharge_lps: {
      label: 'ਪਾਣੀ ਦੀ ਨਿਕਾਸੀ (L/s)',
      help: 'ਫਲੋਮੀਟਰ ਤੋਂ ਸਿੱਧਾ ਨਿਕਾਸੀ ਲੀਟਰ ਪ੍ਰਤੀ ਸਕਿੰਟ ਵਿੱਚ ਪੜ੍ਹੋ।',
    },
    has_issues: { label: 'ਕੀ ਪੰਪ ਵਿੱਚ ਕੋਈ ਖਰਾਬੀ ਹੋਈ?' },
    issues_faced: { label: 'ਆਈਆਂ ਖਰਾਬੀਆਂ (ਜੋ ਲਾਗੂ ਹੋਣ ਉਹ ਚੁਣੋ)' },
    capacitor_available: { label: 'ਕੈਪੈਸੀਟਰ ਪਹਿਲਾਂ ਤੋਂ ਉਪਲਬਧ' },
    canal_irrigated: { label: 'ਨਹਿਰੀ ਸਿੰਚਾਈ?' },
    canal_name: { label: 'ਨਹਿਰ ਦਾ ਨਾਮ' },
    canal_irrigation_days: { label: 'ਪ੍ਰਤੀ ਸੀਜ਼ਨ ਨਹਿਰੀ ਸਿੰਚਾਈ ਦੇ ਦਿਨ' },
    laser_levelling_done_year: { label: 'ਲੇਜ਼ਰ ਜ਼ਮੀਨ ਪੱਧਰੀਕਰਨ ਹੋਇਆ (ਸਾਲ)' },
    laser_levelling_planned_year: { label: 'ਲੇਜ਼ਰ ਜ਼ਮੀਨ ਪੱਧਰੀਕਰਨ ਯੋਜਨਾਬੱਧ (ਸਾਲ)' },
    irrigations_per_acre: { label: 'ਪ੍ਰਤੀ ਏਕੜ ਸਿੰਚਾਈ ਗਿਣਤੀ (ਮੁੱਖ ਫਸਲ)' },
    kharif_crops: {
      label: 'ਖਰੀਫ਼ (ਜੂਨ – ਅਕਤੂਬਰ)',
      help: 'ਸੌਣ-ਰੁੱਤ ਦੀਆਂ ਫਸਲਾਂ — ਚਾਵਲ, ਮੱਕੀ, ਕਪਾਹ, ਦਾਲਾਂ, ਚਾਰਾ। ਘੱਟੋ-ਘੱਟ ਇੱਕ ਫਸਲ ਅਤੇ ਉਸ ਦੀ ਕਿਸਮ ਚੁਣੋ।',
    },
    rabi_crops: {
      label: 'ਹਾੜੀ (ਅਕਤੂਬਰ – ਮਾਰਚ) · ਚੋਣਵੀਂ',
      help: 'ਸਰਦ-ਰੁੱਤ ਦੀਆਂ ਫਸਲਾਂ — ਕਣਕ, ਸਰ੍ਹੋਂ, ਛੋਲੇ, ਮਸਰ।',
    },
    zaid_crops: {
      label: 'ਜ਼ੈਦ (ਮਾਰਚ – ਜੂਨ) · ਚੋਣਵੀਂ',
      help: 'ਗਰਮ-ਰੁੱਤ ਦੀਆਂ ਫਸਲਾਂ — ਚਾਰਾ, ਗਰਮੀ ਦੀਆਂ ਦਾਲਾਂ, ਸਬਜ਼ੀਆਂ।',
    },
    installation_photos: { label: 'ਫੋਟੋਆਂ' },
  },
  issues: {
    bearing_failure_noise_vibration: 'ਬੇਅਰਿੰਗ ਫੇਲ੍ਹ (ਆਵਾਜ਼, ਥਰਥਰਾਹਟ)',
    cavitation_noise_jerks_at_low_water_level: 'ਕੈਵੀਟੇਸ਼ਨ (ਪਾਣੀ ਘਟਣ ਉੱਤੇ ਆਵਾਜ਼, ਝਟਕੇ)',
    clogging_of_strainers_foot_valves_suction_line: 'ਸਟ੍ਰੇਨਰ / ਫੁੱਟ ਵਾਲਵ / ਸਕਸ਼ਨ ਲਾਈਨ ਜਾਮ',
    corrosion_of_metallic_parts: 'ਧਾਤੂ ਹਿੱਸਿਆਂ ਨੂੰ ਜੰਗ',
    dry_running_when_groundwater_falls: 'ਪਾਣੀ ਥੱਲੇ ਜਾਣ ਉੱਤੇ ਸੁੱਕਾ ਚੱਲਣਾ',
    electrical_faults_single_phasing: 'ਬਿਜਲੀ ਖਰਾਬੀ / ਸਿੰਗਲ-ਫੇਜ਼ਿੰਗ',
    impeller_wear_clogging_by_sand_silt_debris: 'ਇੰਪੈਲਰ ਘਸਾਈ / ਰੇਤ, ਮਿੱਟੀ ਨਾਲ ਜਾਮ',
    motor_burn_out_coil_burn: 'ਮੋਟਰ ਜਲਣਾ / ਕੋਇਲ ਜਲਣਾ',
    seal_gland_failure_or_leakage: 'ਸੀਲ / ਗਲੈਂਡ ਫੇਲ੍ਹ ਜਾਂ ਲੀਕ',
    shaft_misalignment_bending: 'ਸ਼ਾਫਟ ਦੀ ਗਲਤ ਅਲਾਇਨਮੈਂਟ / ਮੋੜ',
  },
  crops: {
    rice_non_basmati: 'ਚਾਵਲ (ਨਾਨ-ਬਾਸਮਤੀ)',
    rice_basmati: 'ਚਾਵਲ (ਬਾਸਮਤੀ)',
    maize: 'ਮੱਕੀ',
    bajra: 'ਬਾਜਰਾ',
    cotton: 'ਨਰਮਾ / ਕਪਾਹ',
    moong: 'ਮੂੰਗੀ',
    mash_urdbean: 'ਮਾਂਹ ਦੀ ਦਾਲ',
    arhar: 'ਅਰਹਰ',
    soybean: 'ਸੋਇਆਬੀਨ',
    sugarcane: 'ਗੰਨਾ',
    groundnut: 'ਮੂੰਗਫਲੀ',
    sesame: 'ਤਿਲ',
    sorghum: 'ਜਵਾਰ',
    guinea_grass: 'ਗਿਨੀ ਘਾਹ',
    guara: 'ਗਵਾਰ',
    cowpea: 'ਰਵਾਂਹ',
    wheat: 'ਕਣਕ',
    mustard: 'ਸਰ੍ਹੋਂ / ਰਾਇਆ',
    barley: 'ਜੌਂ',
    gram_chickpea: 'ਛੋਲੇ',
    lentils: 'ਮਸਰ',
    peas: 'ਮਟਰ',
    summer_moong: 'ਗਰਮੀ ਦੀ ਮੂੰਗੀ',
    summer_mash: 'ਗਰਮੀ ਦੀ ਮਾਂਹ',
    fodder_maize: 'ਚਾਰੇ ਵਾਲੀ ਮੱਕੀ',
    fodder_bajra: 'ਚਾਰੇ ਵਾਲਾ ਬਾਜਰਾ',
    fodder_sorghum: 'ਚਾਰੇ ਵਾਲੀ ਜਵਾਰ',
    watermelon: 'ਤਰਬੂਜ਼',
    muskmelon: 'ਖਰਬੂਜ਼ਾ',
    cucumber: 'ਖੀਰਾ',
    bottle_gourd: 'ਘੀਆ',
    bitter_gourd: 'ਕਰੇਲਾ',
    pumpkin: 'ਕੱਦੂ',
    okra: 'ਭਿੰਡੀ',
    __other__: 'ਹੋਰ (ਸੂਚੀ ਵਿੱਚ ਨਹੀਂ)',
  },
  photoSlots: {
    TUBEWELL: {
      label: 'ਟਿਊਬਵੈੱਲ ਦੀ ਫੋਟੋ',
      helper: 'ਥਾਂ ਉੱਤੇ ਪੰਪ / ਬੋਰਵੈੱਲ ਦੀ ਚੌੜੀ ਫੋਟੋ',
    },
    DEVICE_SETUP: {
      label: 'ਯੰਤਰ ਲੱਗਣ ਦੀ ਫੋਟੋ',
      helper: 'ਵਾਟਰ ਯੰਤਰ ਵਾਇਰਿੰਗ ਸਮੇਤ ਲੱਗਾ ਹੋਇਆ',
    },
  },
  // Measurement units. Full-word units are transliterated to Gurmukhi; SI
  // abbreviations (L/sec, kL, m³/hr, LPM) stay Latin because the rep reads
  // them off pump nameplates that are printed in English.
  units: {
    feet: 'ਫੁੱਟ',
    metre: 'ਮੀਟਰ',
    lpm: 'LPM',
    cusec: 'ਕਿਊਸਿਕ',
    'm_hr': 'm³/hr',
    kl: 'kL',
    'l_sec': 'L/sec',
    acres: 'ਏਕੜ',
    sqm: 'm²',
  },
};
